// src/components/Login.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from '../../axiosConfig';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import SEOHead from '../SEOHead';
import UserSelector from '../UserSelector';
import AuthSplitShell from '../auth/AuthSplitShell';
import AuthSeparator from '../auth/AuthSeparator';
import OAuthProviderGrid from '../auth/OAuthProviderGrid';
import { authTitleSx, authSubtitleSx, authLegalTextSx } from '../auth/authSplitShellSx';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { hasStaffRole } from '../../utils/userRoles';
import { Input, Button } from '../ui';
import { acme } from '../../theme/acmeTheme';

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const message = location.state?.message;
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setErrorMessage, snackbarProps } = usePageSnackbar();
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/login', { login, password }, { withCredentials: true });

      if (res.data?.authenticated) {
        if (hasStaffRole(res.data.role)) {
          const sessionResponse = await axios.get('/api/auth/check-session', { withCredentials: true });
          if (sessionResponse.data.authenticated) {
            setCurrentUser(sessionResponse.data.user);
            setShowUserSelector(true);
          } else {
            onLoginSuccess();
            navigate('/dashboard', { replace: true });
          }
        } else {
          onLoginSuccess();
          navigate('/dashboard', { replace: true });
        }
      } else {
        setErrorMessage('Invalid credentials.');
      }
    } catch (error) {
      const status = error.response?.status;
      const serverMsg =
        error.response?.data?.error ||
        (typeof error.response?.data === 'string' ? error.response.data : null);
      if (status && serverMsg) {
        setErrorMessage(`Login failed (${status}): ${serverMsg}`);
      } else if (status) {
        setErrorMessage(`Login failed (HTTP ${status}). Check that the backend is running.`);
      } else {
        setErrorMessage('Login failed. Please check your login and password and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead pageName="login" />
      <AuthSplitShell>
        {message && (
          <Alert severity="info" sx={{ borderRadius: acme.radius.md }}>
            {message}
          </Alert>
        )}

        <Box>
          <Typography component="h1" sx={authTitleSx}>
            Sign in to Mailsball
          </Typography>
          <Typography sx={authSubtitleSx}>
            Access your email & SMS marketing dashboard.
          </Typography>
        </Box>

        <OAuthProviderGrid
          onGoogle={() => { window.location.href = '/api/auth/google'; }}
          onFacebook={() => { window.location.href = '/api/auth/facebook'; }}
          onLinkedIn={() => { window.location.href = '/api/auth/linkedin'; }}
        />

        <AuthSeparator />

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'grid', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.75rem', color: acme.colors.mutedForegroundSolid }}>
            Enter your login and password to sign in
          </Typography>
          <Input
            label="Login"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            required
          />
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="link"
            onClick={() => navigate('/forgot-password')}
            sx={{ justifyContent: 'flex-start', px: 0, minHeight: 'auto', height: 'auto' }}
          >
            Forgot password?
          </Button>

          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Signing in…' : 'Continue with email'}
          </Button>
        </Box>

        <Button component={Link} to="/register" variant="outline" fullWidth size="lg">
          Create account
        </Button>

        <Typography sx={authLegalTextSx}>
          By continuing, you agree to our{' '}
          <Link to="/terms-of-service">Terms of Service</Link> and{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </Typography>
      </AuthSplitShell>

      <PageSnackbars {...snackbarProps} />

      <Dialog open={showUserSelector} onClose={() => setShowUserSelector(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select User Account</DialogTitle>
        <DialogContent>
          <UserSelector
            currentUser={currentUser}
            onUserSwitch={(newUser) => {
              setCurrentUser(newUser);
              setShowUserSelector(false);
              onLoginSuccess();
              navigate('/dashboard', { replace: true });
            }}
            onClose={() => {
              setShowUserSelector(false);
              onLoginSuccess();
              navigate('/dashboard', { replace: true });
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Login;
